import Typography from '@ds/components/typography/Typography';
import {
    arrow,
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    safePolygon,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
} from '@floating-ui/react';
import clsx from 'clsx';
import React, { type ReactNode, useRef, useState } from 'react';

import styles from './Tooltip.module.css';

export type Placement = 'top' | 'bottom' | 'left' | 'right';

export type TooltipProps = {
    title?: string;
    text: string;
    children: ReactNode;
    placement?: Placement;
    /**
     * Makes the tooltip a toggletip: a click, tap, Enter or Space on the trigger also opens it and keeps it open
     * until Escape, a second click or a press outside, as well as the usual hover and focus. Use it only when the
     * trigger has no action of its own, so that pressing it does something (shows the text) and a touch user,
     * who has no hover, can reach the text. The trigger should announce as a button.
     *
     * @default false
     */
    toggle?: boolean;
};

// Matches `--space-3`'s desktop px value — the gap only needs to be roughly
// token-sized, not pixel-exact (the SortMenu/FilterMenu offset() calls are hard-coded the same way).
const TOOLTIP_OFFSET = 12;

/**
 * Hover/focus tooltip, positioned by the shared floating-ui engine (see the
 * Positioning Engine glossary entry / ADR 0019). The bubble opens on
 * mouse-over or when the wrapped trigger receives keyboard focus, and Escape
 * dismisses it without moving focus. `flip`/`shift` keep it on-screen and
 * `FloatingPortal` escapes any clipping ancestor (e.g. a table row); while
 * open, the bubble carries `role="tooltip"` and a single-element child is
 * pointed at it via `aria-describedby`, so screen readers announce the text
 * from the trigger. With `toggle` it is a toggletip: a click, tap, Enter or Space pins it open as well, and a
 * press outside closes it.
 */
const Tooltip = ({ title, text, children, placement = 'top', toggle = false }: TooltipProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const arrowRef = useRef<HTMLDivElement>(null);
    // `useId()` yields colons, which are awkward in selectors — strip them.
    const tooltipId = `tooltip-${React.useId().replace(/:/g, '')}`;

    const {
        refs,
        floatingStyles,
        context,
        placement: resolvedPlacement,
        middlewareData,
    } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(TOOLTIP_OFFSET),
            flip({ padding: 8 }),
            shift({ padding: 8 }),
            arrow({ element: arrowRef, padding: 8 }),
        ],
    });

    // `safePolygon` keeps the bubble open while the pointer travels from the trigger onto it and while it
    // rests there, so the text can be read or selected; it closes once the pointer has left both.
    const hover = useHover(context, { handleClose: safePolygon() });
    const focus = useFocus(context);
    // A toggletip pins open on click. A click after hover has already opened it pins it rather than closing it
    // (`stickIfOpen`), and once pinned, leaving the trigger no longer closes it. The library's own keyboard
    // handling is off: it toggles, so Enter would close a tooltip that focus has just opened. Enter and Space
    // only ever open it here (see handleKeyDown), and Escape closes it.
    const click = useClick(context, { enabled: toggle, keyboardHandlers: false });
    // A plain tooltip closes on mouseleave, blur or Escape only. A pinned toggletip has no mouseleave to close it,
    // and a touch user has no Escape, so a press outside closes it too.
    const dismiss = useDismiss(context, { outsidePress: toggle });
    const role = useRole(context, { role: 'tooltip' });

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, click, dismiss, role]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!toggle || event.defaultPrevented) return;
        if (event.key === 'Enter' || event.key === ' ') {
            // Space would scroll the page: the trigger is not a native button.
            event.preventDefault();
            context.onOpenChange(true, event.nativeEvent, 'click');
        }
    };

    // `flip` only ever resolves to a base side here (no `-start`/`-end` alignment requested),
    // but split defensively since floating-ui's Placement type allows for it.
    const resolvedSide = resolvedPlacement.split('-')[0] as Placement;
    const { x: arrowX, y: arrowY } = middlewareData.arrow ?? {};

    const child =
        React.isValidElement(children) && isOpen
            ? React.cloneElement(children as React.ReactElement, {
                  'aria-describedby':
                      clsx((children as React.ReactElement).props['aria-describedby'], tooltipId) || undefined,
              })
            : children;

    return (
        <div
            ref={refs.setReference}
            className={styles.container}
            {...getReferenceProps({ onKeyDown: handleKeyDown })}
        >
            {child}
            {isOpen && (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        className={styles.tooltip}
                        data-placement={resolvedSide}
                        style={floatingStyles}
                        {...getFloatingProps()}
                        id={tooltipId}
                    >
                        <div className={styles.content}>
                            {title && (
                                <Typography
                                    variant="heading-3"
                                    color="inverse"
                                >
                                    {title}
                                </Typography>
                            )}
                            <Typography
                                variant="body-2"
                                color="inverse"
                            >
                                {text}
                            </Typography>
                        </div>
                        <div
                            ref={arrowRef}
                            className={styles.arrow}
                            style={{
                                left: arrowX != null ? `${arrowX}px` : undefined,
                                top: arrowY != null ? `${arrowY}px` : undefined,
                            }}
                        />
                    </div>
                </FloatingPortal>
            )}
        </div>
    );
};

export { Tooltip };
export default Tooltip;
